import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';

import { useModo } from '../config/modo';
import { useTranslation } from 'react-i18next';

/**
 * ESTA PÁGINA NO EXISTE.
 *
 * 🔴 POR QUÉ HIZO FALTA (1-sep). Cualquier dirección equivocada
 * —`/una-pagina-que-no-existe`— pintaba **solo el pie**: ni mensaje, ni salida,
 * ni forma de saber si el sitio se había roto. El Router no tenía ruta comodín,
 * así que ninguna coincidía y no se renderizaba nada.
 *
 * No es un caso raro: pasa con un enlace viejo, con un enlace compartido que
 * cambió de dirección y con una errata al teclear. Y una canalización que no
 * existe SÍ estaba resuelta desde hacía tiempo —«Esa canalización no está
 * aquí», con su botón—, así que el sitio ya sabía tratar bien este momento en
 * un sitio y no en el otro.
 *
 * ⚠️ SIN HUMOR Y SIN «404». A quien llega aquí no le importa el número, y este
 * sitio recibe a gente que llega dolida: no es el lugar para una broma ni para
 * jerga de informático. Se dice lo que ha pasado, se pide disculpa sin
 * dramatizar y se ofrecen las dos puertas que de verdad usa alguien perdido —el
 * mensaje del día y la biblioteca—, que son las mismas que ofrece la portada.
 */
const NoEstaPage = () => {
  const { t } = useTranslation();
  const { modo } = useModo();
  const location = useLocation();

  return (
    <Box className={`no-esta no-esta-${modo}`} sx={{
      background: 'var(--fondo)',
      padding: 'clamp(60px, 12vw, 140px) 0 clamp(80px, 14vw, 160px)',
      minHeight: '52vh'
    }}>
      <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
        <Typography component="h1" sx={{
          fontFamily: 'var(--fuente-lectura)', fontWeight: 300,
          fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', lineHeight: 1.15,
          color: 'var(--texto)', mb: 2
        }}>
          {t('noesta.titulo')}
        </Typography>

        <Typography sx={{
          fontFamily: 'var(--fuente-lectura)',
          fontSize: 'clamp(1rem, 2.4vw, 1.1rem)', lineHeight: 1.75,
          color: 'var(--texto-suave)', mb: 1
        }}>
          {t('noesta.texto')}
        </Typography>

        {/* La dirección que se pidió, en pequeño: quien llega desde un enlace
            roto necesita poder decir CUÁL para que se arregle. */}
        <Typography sx={{
          fontFamily: 'var(--fuente-titulo)', fontSize: '.8rem',
          color: 'var(--texto-tenue)', mb: 5, wordBreak: 'break-all'
        }}>
          {location.pathname}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button component={RouterLink} to="/" variant="outlined" sx={{
            borderColor: 'var(--linea)', color: 'var(--texto)',
            '&:hover': { borderColor: 'var(--acento)', color: 'var(--acento)' }
          }}>
            {t('noesta.hoy')}
          </Button>
          <Button component={RouterLink} to="/canalizaciones" variant="outlined" sx={{
            borderColor: 'var(--linea)', color: 'var(--texto)',
            '&:hover': { borderColor: 'var(--acento)', color: 'var(--acento)' }
          }}>
            {t('noesta.biblioteca')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default NoEstaPage;
